import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, Form, Input, Popconfirm, Table } from 'antd';
import AxiosCaller from '../../../authenticatie/AxiosCaller';
import { Plus, Trash2 } from 'lucide-react';
import { openNotificationSuccess, openNotificationError } from '../../../Components/Notification/Notification';



const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);
  
  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };
  
  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.error('Save failed:', errInfo);
    }
  };
  
  let childNode = children;
  
  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{ margin: 0 }}
        name={dataIndex}
        rules={[{ required: true, message: `${title} is required.` }]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div className="editable-cell-value-wrap" style={{ paddingInlineEnd: 24 }} onClick={toggleEdit}>
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const KlasTypeTable = () => {
  const { get, put, del, post } = AxiosCaller();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const response = await get('/klasType');
      setTypes(response.data);
    } catch (error) {
      console.error('Error fetching klasTypes:', error);
      openNotificationError("Fout bij ophalen van klasTypes", error)
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (key) => {
    try {
      await del(`/klasType/${key}`);
      openNotificationSuccess("Klastype succesvol verwijderd")
      await fetchTypes()

    } catch (error) {
      console.error('Error deleting klasType:', error);
      openNotificationError('Fout bij verwijderen van klastype', error)
    }
  };

  const handleSave = async (row) => {
    try {
      await put(`/klasType/${row._id}`, { naam: row.naam });
      const newData = types.map((item) => (item._id === row._id ? row : item));
      setTypes(newData);
    } catch (error) {
      console.error('Error updating klasType:', error);
      openNotificationError('Fout bij aanpassen van klastype', error)
    }
  };

  const handleAdd = async () => {
    try {
      const newType = { naam: 'Nieuw KlasType' };
      const response = await post('/klasType', newType);
      setTypes([{ ...response.data, key: response.data._id }, ...types]);
    } catch (error) {
      console.error('Error adding klasType:', error);
      openNotificationError("Fout bij opslaan van klastype", error)
    }
  };
  
  const columns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      editable: true,
      sorter: (a, b) => a.naam.localeCompare(b.naam),
    },
    {
      title: 'Acties',
      dataIndex: 'operation',
      width: '15%',  // Correcte manier om de breedte in te stellen
      render: (_, record) => (
        <Popconfirm title="Weet je het zeker?" onConfirm={() => handleDelete(record._id)}>
          <Trash2 className='delete-button'/>
        </Popconfirm>
      ),
    },
  ];
  
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columnConfig = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div className='Screen-container'>
      <div className='space-between'>
        <h1 className='title'>Klastype</h1>
        <Button onClick={handleAdd} type="primary" className='button'>
          <Plus /> Klastype toevoegen
        </Button>
      </div>
      
      <Table
        loading={loading}
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={types.map((type) => ({ ...type, key: type._id }))}
        columns={columnConfig}
        pagination={false} // Pagination verwijderd
        scroll={{ y: 0.5 * window.innerHeight}}
      />
    </div>
  );
};

export default KlasTypeTable;